import { useQueryClient } from '@tanstack/react-query';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { DeleteConfirmation } from '@adflow/frontend/feat-facebook-ads';
import { gmbTemplateKeys } from '@adflow/frontend/feat-google-my-business';
import { useCallout } from '@adflow/frontend/ui-common';
import type { DataTableProps } from '@adflow/frontend/ui-data-table';
import { SidebarTable } from '@adflow/frontend/ui-data-table';
import { LoadingIndicator } from '@adflow/frontend/ui-graphics';
import type { PageHeaderProps } from '@adflow/frontend/ui-layout';
import { PageHeader } from '@adflow/frontend/ui-layout';
import { su, theme } from '@adflow/frontend/ui-theme';
import {
	createErrorToast,
	createSuccessToast,
} from '@adflow/frontend/ui-toasts';
import { useAbility } from '@adflow/frontend/util-auth';
import {
	useDispatchNavAction,
	useGetNavActionUrl,
} from '@adflow/frontend/util-router';
import { useWebsocketListener } from '@adflow/frontend/util-websockets';
import type { ViewGMBTemplateDto } from '@adflow/shared/data-google-my-business';
import { ServerToClientEvents } from '@adflow/shared/util-websockets';

import { GMBTemplateFormAdd } from './GMBTemplateFormAdd';
import { GMBTemplateFormEdit } from './GMBTemplateFormEdit';
import {
	goToAddGMBTemplate,
	goToEditGMBTemplate,
	goToGMBTemplatesList,
} from './GMBTemplateNavActions';
import { useGMBTemplatesList } from './useGMBTemplatesList';

export default function GMBTemplateView() {
	const { t } = useTranslation(['common', 'facebook', 'google-ads']);

	const queryClient = useQueryClient();

	const { templateId = 'add' } = useParams();

	const { ability, missingPermissionText } = useAbility();

	const [confirmDelete, setConfirmDelete] = useState(false);
	const onDelete = useCallback(() => setConfirmDelete(true), []);

	const canCreateGMBTemplates = useMemo(
		() => ability.can('create', 'GMB/Templates'),
		[ability]
	);

	const {
		callout: missingCreateGMBTemplatesPermCallout,
		setShowViewCallout: showNoCreateGMBTemplatesPermCallout,
	} = useCallout({
		text: missingPermissionText('create', 'GMB/Templates'),
	});

	const { dataTableProps } = useGMBTemplatesList({ onDelete });

	const navigate = useDispatchNavAction();
	const getNavActionUrl = useGetNavActionUrl();

	const goToAdd = useCallback(() => {
		navigate(goToAddGMBTemplate);
	}, [navigate]);

	const onAddClick = useCallback<Required<PageHeaderProps>['onAddClick']>(
		(e) => {
			if (!canCreateGMBTemplates) {
				showNoCreateGMBTemplatesPermCallout(e);
				return;
			}
			goToAdd();
		},
		[canCreateGMBTemplates, goToAdd, showNoCreateGMBTemplatesPermCallout]
	);

	const onRowClick: NonNullable<
		DataTableProps<ViewGMBTemplateDto>['onRowClick']
	> = useCallback(
		(row, e) => {
			const navAction = goToEditGMBTemplate(row.id);
			if (e?.ctrlKey) {
				window.open(getNavActionUrl(navAction), '_blank');
			} else {
				navigate(navAction);
			}
		},
		[getNavActionUrl, navigate]
	);

	useWebsocketListener(
		ServerToClientEvents.GoogleMyBusinessTemplateUploaded,
		(data) => {
			// Revalidate history list for the template
			void queryClient.invalidateQueries({
				queryKey: gmbTemplateKeys.history(data.templateId, {
					sort: 'createdAt',
					sortDirection: 'desc',
				}),
			});

			// Revalidate the template view
			void queryClient.invalidateQueries({
				queryKey: gmbTemplateKeys.view(data.templateId),
			});

			const isErrors = data.results.some((result) => result.errors.length > 0);

			const formattedErrors = data.results.map((result) => {
				const [avbId, name, address] = result.location.split(' :: ');

				return (
					<tr key={result.location}>
						<td valign="top" css={{ padding: '0.5rem' }}>
							<span css={{ wordBreak: 'normal' }}>
								{name} ({avbId})
							</span>
							<br />
							<span css={su.textSm}>{address}</span>
						</td>
						<td valign="top" css={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
							{result.status}
						</td>
						<td valign="top" css={{ padding: '0.5rem' }}>
							<ul css={{ margin: 0 }}>
								{result.errors.length ? (
									result.errors.map((error, index) => (
										<li key={index}>{error}</li>
									))
								) : (
									<li>{t('facebook:facebookAdsUpload.resultNoError')}</li>
								)}
							</ul>
						</td>
					</tr>
				);
			});

			const message = (
				<div css={{ overflowX: 'auto' }}>
					<table
						css={{
							width: '100%',
							borderCollapse: 'collapse',
							textAlign: 'left',
							fontFamily: 'inherit',
						}}
					>
						<thead>
							<tr>
								<th
									css={{
										padding: '0.5rem',
										whiteSpace: 'nowrap',
										fontWeight: 700,
									}}
								>
									{t('google-ads:location')}
								</th>
								<th
									css={{
										padding: '0.5rem',
										whiteSpace: 'nowrap',
										fontWeight: 700,
									}}
								>
									{t('facebook:status')}
								</th>
								<th
									css={{
										padding: '0.5rem',
										whiteSpace: 'nowrap',
										fontWeight: 700,
									}}
								>
									{t('facebook:errors')}
								</th>
							</tr>
						</thead>
						<tbody>{formattedErrors}</tbody>
					</table>
				</div>
			);

			if (formattedErrors.length > 0) {
				if (isErrors) {
					createErrorToast(message, {
						autoClose: false,
					});
				} else {
					createSuccessToast(message, 60);
				}
			}
		}
	);

	return (
		<>
			<SidebarTable
				{...dataTableProps}
				onRowClick={onRowClick}
				focusedKeyAndValue={
					templateId === 'add'
						? undefined
						: {
								key: 'id',
								value: Number.parseInt(templateId),
							}
				}
				useColumnResizing
				header={
					<PageHeader
						title={t('common:sections.google-my-business-templates')}
						titleNavAction={goToGMBTemplatesList}
						onAddClick={onAddClick}
						addDisabled={!canCreateGMBTemplates}
					/>
				}
				content={
					<Suspense
						fallback={
							<LoadingIndicator
								containerStyle={{
									backgroundColor: theme.colors.background.form,
								}}
							/>
						}
					>
						{templateId ? (
							templateId === 'add' ? (
								<GMBTemplateFormAdd />
							) : (
								<GMBTemplateFormEdit
									key={templateId}
									templateId={Number(templateId)}
								/>
							)
						) : null}
					</Suspense>
				}
			/>
			{missingCreateGMBTemplatesPermCallout}
			<DeleteConfirmation
				show={confirmDelete}
				setShow={setConfirmDelete}
				goTo={goToGMBTemplatesList}
				type="GMB"
			/>
		</>
	);
}
